import { motion } from "framer-motion";
import { ArrowRight, Sun, Heart, Star } from "lucide-react";
import { Reveal } from "./Reveal";

const IMG_BIG =
  "https://images.unsplash.com/photo-1786292949404-084cbd10c7b1?crop=entropy&cs=srgb&fm=jpg&q=85";
const IMG_SMALL =
  "https://images.unsplash.com/photo-1777056491418-d4ff81a4ad92?crop=entropy&cs=srgb&fm=jpg&q=85";

const CHIPS = [
  "Empathy-led teaching",
  "Activity-based learning",
  "Strong parent partnerships",
  "Social-emotional focus",
];

export const About = () => (
  <section id="about" data-testid="about-section" className="relative py-24 lg:py-32 overflow-hidden">
    <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-2 gap-16 lg:gap-20 items-center">
      <div className="relative">
        <Reveal>
          <div className="relative rounded-[2.5rem] overflow-hidden shadow-[0_32px_80px_rgba(45,106,79,0.18)] rotate-[-2deg]">
            <img
              src={IMG_BIG}
              alt="Teacher guiding a happy child at Kiwi Kids"
              className="w-full h-[26rem] sm:h-[30rem] object-cover"
              loading="lazy"
            />
          </div>
        </Reveal>
        <Reveal delay={0.2} className="absolute -bottom-10 -right-2 sm:right-8 w-44 sm:w-56">
          <div className="rounded-[1.75rem] overflow-hidden border-[6px] border-cream shadow-xl rotate-[4deg]">
            <img
              src={IMG_SMALL}
              alt="Children learning together in class"
              className="w-full h-36 sm:h-44 object-cover"
              loading="lazy"
            />
          </div>
        </Reveal>
        <motion.div
          animate={{ rotate: 360 }}
          transition={{ repeat: Infinity, duration: 18, ease: "linear" }}
          className="absolute -top-8 -left-4 w-24 h-24 rounded-full bg-sun flex items-center justify-center shadow-lg"
        >
          <Sun className="w-8 h-8 text-ink" />
        </motion.div>
        <div className="absolute top-6 -right-3 sm:-right-6 glass rounded-2xl px-4 py-3 flex items-center gap-2 shadow-lg">
          <Star className="w-4 h-4 text-peach fill-peach" />
          <span className="text-xs font-semibold text-ink">Play first, always</span>
        </div>
      </div>

      <div>
        <Reveal>
          <span className="inline-block text-xs font-bold tracking-[0.24em] uppercase text-peach mb-4">
            Our Philosophy
          </span>
          <h2 className="font-display font-extrabold tracking-tight text-3xl sm:text-4xl lg:text-5xl leading-[1.08] text-ink">
            Early childhood is{" "}
            <em className="font-quote italic font-medium text-sage">full of firsts.</em>
          </h2>
        </Reveal>
        <Reveal delay={0.12}>
          <p className="mt-6 text-base sm:text-lg text-ink/65 leading-relaxed">
            A first word, a first friend, a first masterpiece on the wall. Our aim is to make
            early learning warm, engaging and full of opportunities to ask questions, try
            things out — and celebrate progress at every stage.
          </p>
        </Reveal>
        <Reveal delay={0.22}>
          <div className="mt-8 flex flex-wrap gap-2.5">
            {CHIPS.map((c) => (
              <span
                key={c}
                className="inline-flex items-center gap-2 bg-white border border-ink/8 rounded-full px-4 py-2.5 text-sm font-medium text-ink/75 shadow-sm"
              >
                <Heart className="w-3.5 h-3.5 text-peach fill-peach" />
                {c}
              </span>
            ))}
          </div>
        </Reveal>
        <Reveal delay={0.3}>
          <a
            href="#programs"
            onClick={(e) => {
              e.preventDefault();
              window.__lenis
                ? window.__lenis.scrollTo("#programs", { offset: -80, duration: 1.4 })
                : document.querySelector("#programs")?.scrollIntoView({ behavior: "smooth" });
            }}
            className="group inline-flex items-center gap-2 mt-9 font-semibold text-sage hover:text-peach transition-colors"
          >
            See how a Kiwi Kids day unfolds
            <ArrowRight className="w-5 h-5 transition-transform duration-300 group-hover:translate-x-1.5" />
          </a>
        </Reveal>
      </div>
    </div>
  </section>
);
